import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IoMic, IoMicOff, IoExitOutline, IoRadioButtonOn, IoMusicalNotes, IoPlay, IoPause, IoRepeat, IoStop, IoSync, IoVolumeHigh, IoVideocam, IoVideocamOff, IoGrid, IoDesktop, IoDesktopOutline, IoPersonCircle, IoPlaySkipForward, IoShuffle, IoTrash, IoClose, IoChevronDown, IoChevronUp, IoArrowUp, IoArrowDown, IoAdd, IoRecording, IoRecordingOutline, IoDocumentText, IoChevronForward, IoChevronBack, IoPeople, IoTv } from 'react-icons/io5';
import '../style/Groups.css';
import Avatar from './Avatar';
import { useSocket } from '../socket/SocketContext';
import { useVoiceConnection } from '../hooks/useVoiceConnection';
import { useLyricsSyncOffset } from '../hooks/useLyricsPosition';
import { useLanguage } from '../i18n/LanguageContext';
import { useLayoutMode } from '../hooks/useLayoutMode';
import { useConfirm } from '../hooks/useConfirm';
import { useBackToClose } from '../hooks/useBackToClose';
import { useWakeLock } from '../hooks/useWakeLock';
import MusicSearchModal from './MusicSearchModal';
import VideoGridOverlay from './VideoGridOverlay';
import VideoWatchOverlay from './VideoWatchOverlay';
import ScreenShareOverlay from './ScreenShareOverlay';
import ScreenSharePicker from './ScreenSharePicker';
import RoomReactions from './RoomReactions';
import LyricsPanel from './LyricsPanel';
import LyricsOverlay from './LyricsOverlay';
import { cleanMusicTitle } from '../utils/musicTitle';

const REFRESH_INTERVAL_MS = 8 * 60 * 1000; // token is ~10min, refresh with margin


// The volume slider's raw drag position (0..1) isn't mapped 1:1 to the
// actual 0..3 (0%-300%) volume value - a plain linear mapping crams every
// percent below ~5% into a couple of pixels, making something like "exactly
// 2%" nearly impossible to land on with a mouse. Instead the left half of
// the slider covers 0%-30% (the range people actually fine-tune) and the
// right half covers the remaining 30%-300% "boost" range, which nobody
// reported trouble with at coarse resolution.
const VOLUME_KNEE_POSITION = 0.5;
const VOLUME_KNEE_VALUE = 0.3;
const VOLUME_MAX = 3;

function positionToVolume(t) {
  if (t <= VOLUME_KNEE_POSITION) return (t / VOLUME_KNEE_POSITION) * VOLUME_KNEE_VALUE;
  return VOLUME_KNEE_VALUE + ((t - VOLUME_KNEE_POSITION) / (1 - VOLUME_KNEE_POSITION)) * (VOLUME_MAX - VOLUME_KNEE_VALUE);
}

function volumeToPosition(v) {
  if (v <= VOLUME_KNEE_VALUE) return (v / VOLUME_KNEE_VALUE) * VOLUME_KNEE_POSITION;
  return VOLUME_KNEE_POSITION + ((v - VOLUME_KNEE_VALUE) / (VOLUME_MAX - VOLUME_KNEE_VALUE)) * (1 - VOLUME_KNEE_POSITION);
}

// One-tap levels for the phone sheet's volume row - a finger on a slider
// can't hit "exactly 100%" the way a mouse wheel can.
const VOLUME_PRESETS = [0, 0.5, 1, 2];

// Plain text-overflow:ellipsis (the previous behavior) silently hides
// whatever doesn't fit - fine for a room name, not for a song title someone
// actually wants to read. Measures itself against its own clipped
// container on every title change and only turns the scrolling animation on
// when the text genuinely doesn't fit; a title that already fits stays
// perfectly still instead of scrolling pointlessly.
function MarqueeText({ text, className }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [overflowPx, setOverflowPx] = useState(0);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    setOverflowPx(Math.max(0, inner.scrollWidth - outer.clientWidth));
  }, [text]);

  return (
    <b className={className} ref={outerRef}>
      <span
        ref={innerRef}
        className={overflowPx > 0 ? 'MarqueeTextInner MarqueeTextScroll' : 'MarqueeTextInner'}
        style={overflowPx > 0 ? { '--marquee-distance': `-${overflowPx}px` } : undefined}
      >
        {text}
      </span>
    </b>
  );
}

// room.name alone isn't enough for personal rooms - it's always the literal
// generic string "Personal Room" in the DB (see Backend/controllers/
// groupController.js), never per-user. presence:sync already embeds a
// room.owner {userId, username, fullName, imageUrl} object for personal
// rooms (same data Groups.js's RoomCard uses for its owner name/crown), so
// this mirrors that instead of showing the same undifferentiated label for
// every member's room. Public rooms keep room.name as-is - already
// distinctly named ("Public Room 1"/"Public Room 2").
function roomLabel(groupState, t) {
  const { currentRoomId, roomsByGroup } = groupState;
  for (const rooms of Object.values(roomsByGroup)) {
    const room = rooms.find((r) => r.roomId === currentRoomId);
    if (!room) continue;
    if (room.type === 'personal' && room.owner) {
      return t('voice.personalRoomLabel', { name: room.owner.fullName || room.owner.username });
    }
    return room.name;
  }
  return t('voice.defaultRoomLabel');
}

// Occupants aren't a flat groupState field - each room carries its own list
// nested under roomsByGroup (see groupReducer.js), so finding "the current
// room's occupants" means the same lookup as roomLabel above. Used only to
// resolve avatar imageUrls for VideoGridOverlay's camera-off fallback.
function roomOccupants(groupState) {
  const { currentRoomId, roomsByGroup } = groupState;
  for (const rooms of Object.values(roomsByGroup)) {
    const room = rooms.find((r) => r.roomId === currentRoomId);
    if (room) return room.occupants;
  }
  return [];
}

export default function VoiceRoomPanel() {
  const {
    groupState, leaveRoom, refreshVoiceToken, pauseMusic, resumeMusic, setMusicLoop, stopMusic,
    skipMusic, removeFromMusicQueue, clearMusicQueue, moveInMusicQueue, shuffleMusicQueue,
    startTranscript, stopTranscript,
    playVideo, pauseVideo, seekVideo, stopVideo, reportVideoBuffering, reportVideoReady
  } = useSocket();
  const { t } = useLanguage();
  const [confirm, confirmEl] = useConfirm();
  const { currentRoomId, voiceToken, musicState, videoWatchState, transcriptState, transcriptPartials } = groupState;
  const active = !!currentRoomId;
  const [showMusicSearch, setShowMusicSearch] = useState(false);
  // Collapsed by default: this panel is a fixed 320px column that already
  // splits its height between the participant list and the player, so the
  // queue only takes vertical space once someone asks to see it.
  const [showQueue, setShowQueue] = useState(false);
  const musicQueue = (musicState && musicState.queue) || [];
  const [showLyricsOverlay, setShowLyricsOverlay] = useState(false);
  // Single shared instance - see useLyricsPosition.js's comment on why this
  // must not be called independently inside both LyricsPanel and
  // LyricsOverlay (that was the actual bug: two out-of-sync local copies).
  const lyricsSync = useLyricsSyncOffset();

  // Phones get a different shell around the same state and connection: a
  // mini bar (landscape: a side rail) that opens a full-screen tabbed sheet
  // (landscape: a drawer), instead of the 320px rail - which on a phone
  // either covered the page or got squeezed into an unusable strip. See the
  // isMobile branch of the render below.
  const { isMobile, isMobileLandscape } = useLayoutMode();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetTab, setSheetTab] = useState('people');
  // Whose volume row is expanded in the sheet - a 0-300% slider on every
  // row doesn't fit a phone's width next to the name, so it's tap-to-open.
  const [volumeOpenFor, setVolumeOpenFor] = useState(null);
  useEffect(() => {
    if (active) return;
    setSheetOpen(false);
    setVolumeOpenFor(null);
  }, [active]);
  useBackToClose(isMobile && sheetOpen, () => setSheetOpen(false));
  // In a room, the screen stays on (every platform) - see useWakeLock.
  useWakeLock(active);

  // Collapsed by default, same rationale as showQueue - the panel is small
  // and the transcript can grow long over a session.
  const [showTranscript, setShowTranscript] = useState(false);
  const transcriptActive = !!(transcriptState && (transcriptState.status === 'active' || transcriptState.status === 'connecting'));
  const transcriptSegments = (transcriptState && transcriptState.recentSegments) || [];
  const transcriptPartialList = Object.entries(transcriptPartials || {}).map(([userId, p]) => ({ userId, ...p }));
  const transcriptVisible = transcriptActive || transcriptSegments.length > 0;
  // The transcript tab only exists while there's a transcript - fall back
  // rather than leave the sheet on a tab that's no longer in the bar.
  const tab = sheetTab === 'transcript' && !transcriptVisible ? 'people' : sheetTab;
  // In the sheet the transcript has its own tab, so it's never collapsed.
  const transcriptExpanded = isMobile || showTranscript;
  // Auto-scrolls to the newest line as the session progresses - only while
  // actually expanded, so a collapsed transcript doesn't fight the user's
  // own scroll position the moment they open it back up mid-session.
  const transcriptListRef = useRef(null);
  const transcriptPartialsKey = transcriptPartialList.map((p) => `${p.userId}:${p.text}`).join('|');
  useEffect(() => {
    if (!transcriptExpanded || !transcriptListRef.current) return;
    transcriptListRef.current.scrollTop = transcriptListRef.current.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcriptExpanded, sheetOpen, tab, transcriptSegments.length, transcriptPartialsKey]);

  const {
    connected, participants, muted, toggleMute, devices, selectedDeviceId, selectDevice, setParticipantVolume, error,
    cameraOn, toggleCamera, videoTracks, videoDevices, selectedVideoDeviceId, selectVideoDevice,
    screenShareOn, toggleScreenShare, screenShareTracks, screenShareAudioTracks
  } = useVoiceConnection(voiceToken, currentRoomId, active);

  const anyCamera = participants.some((p) => p.cameraEnabled);
  // At most one participant is ever spotlighted at a time (see the overlay
  // priority chain below) - if LiveKit somehow reports more than one
  // simultaneously (a race between one stopping and another starting), the
  // first one found just wins rather than the UI trying to show both.
  const screenSharer = participants.find((p) => p.screenSharing);
  const screenSharingActive = !!screenSharer;
  // Cameras are shown inline (as a small strip under the video, see
  // VideoWatchOverlay) whenever a watch-together session is active, so the
  // standalone full-size grid below is suppressed then - otherwise the two
  // position:fixed overlays would stack on top of each other.
  const watchingTogether = !!(videoWatchState && videoWatchState.status !== 'idle');
  // Shared "is the camera grid / screen-share overlay visible" toggle - both
  // ScreenShareOverlay and VideoGridOverlay's onClose set this the same way,
  // so it has to gate both of their render conditions below too. It used to
  // only gate the camera grid's - ScreenShareOverlay rendered on
  // screenSharingActive alone, so its close button set this but nothing
  // actually read it for that overlay, making it impossible to dismiss a
  // screen share while it was still running.
  const [showVideo, setShowVideo] = useState(false);
  // Watch-together's "minimize" is local and keeps the player mounted (just
  // hidden) - it keeps playing, keeps its sound and keeps taking part in the
  // room's buffering barrier, so hiding it can't stall everyone else. Ending
  // the session for everyone is a separate, confirmed action. Resets with
  // the session, so the next video opens visible again.
  const [watchMinimized, setWatchMinimized] = useState(false);
  useEffect(() => {
    if (!watchingTogether) setWatchMinimized(false);
  }, [watchingTogether]);
  // Auto-opens on the rising edge only (false -> true) of "there's now
  // something to show" (a camera turned on, or someone started screen
  // sharing) - once the user manually collapses it, it must stay collapsed
  // even though that thing is still active, or clicking the collapse button
  // would look like it does nothing. Self-resets correctly on room switches
  // too: both go false while disconnected/reconnecting (participants
  // briefly empty), so the next room's first camera/share is always a fresh
  // false->true edge.
  const overlayActive = anyCamera || screenSharingActive;
  const overlayActivePrevRef = useRef(false);
  useEffect(() => {
    if (overlayActive && !overlayActivePrevRef.current) setShowVideo(true);
    overlayActivePrevRef.current = overlayActive;
  }, [overlayActive]);

  // Lets the wheel handler below always read each participant's *current*
  // volume without needing to be re-created (and re-attached to the DOM)
  // every time it changes - see getVolumeWheelHandler.
  const participantsRef = useRef(participants);
  participantsRef.current = participants;

  // One native (non-passive) wheel listener per participant, cached by
  // identity so the same function reference is handed to addEventListener
  // on every render - the DOM spec treats re-adding an identical
  // (function, options) pair as a no-op, so this can't stack duplicate
  // listeners even though the ref callback below runs on every render.
  // A plain React onWheel prop can't be used here: React registers wheel
  // listeners as passive by default, so e.preventDefault() inside one would
  // both warn in the console and fail to stop the page/panel from also
  // scrolling while the user spins the wheel over the slider.
  const wheelHandlersRef = useRef(new Map());
  const getVolumeWheelHandler = (identity) => {
    if (!wheelHandlersRef.current.has(identity)) {
      wheelHandlersRef.current.set(identity, (e) => {
        e.preventDefault();
        const current = participantsRef.current.find((x) => x.identity === identity);
        if (!current) return;
        const step = e.deltaY < 0 ? 0.02 : -0.02;
        const nextPosition = Math.min(1, Math.max(0, volumeToPosition(current.volume) + step));
        setParticipantVolume(identity, positionToVolume(nextPosition));
      });
    }
    return wheelHandlersRef.current.get(identity);
  };

  useEffect(() => {
    if (!active) return undefined;
    const interval = setInterval(refreshVoiceToken, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [active, refreshVoiceToken]);

  // Tell the main process whether we're in a room, for the quit-confirmation
  // prompt, and leave gracefully on system sleep instead of holding a dead
  // WebRTC session across it.
  useEffect(() => {
    if (!window.cyclone) return undefined;
    window.cyclone.setInRoom(active);
    if (!active) return undefined;
    const unsubscribe = window.cyclone.onSuspend(() => leaveRoom());
    return unsubscribe;
  }, [active, leaveRoom]);

  if (!active) return null;

  const occupants = roomOccupants(groupState);
  const label = roomLabel(groupState, t);

  // ----- Pieces shared by the desktop rail and the phone sheet -----
  // Each is placed in the desktop markup exactly where it always was, so the
  // desktop DOM is unchanged; the phone branch arranges the same pieces into
  // tabs instead.

  const statusEl = (
    <span className="descGray VoicePanelStatus">
      <span className={`VoicePanelStatusDot ${connected ? '' : 'VoicePanelStatusConnecting'}`} />
      {connected ? t('voice.connected') : t('voice.connecting')}
      {connected && <>&middot; {t('voice.participantCount', { count: participants.length })}</>}
    </span>
  );

  // Always visible while a transcription session is running - anyone in the
  // room needs to know their voice is being written down, regardless of
  // whether they've opened the transcript panel itself (showTranscript only
  // controls the read-back UI).
  const recordingBadgeEl = transcriptActive && (
    <div className="TranscriptRecordingBadge">
      <IoRecording className="TranscriptRecordingDot" />
      {t('transcript.recordingBadge', { name: transcriptState.startedBy ? transcriptState.startedBy.username : '' })}
    </div>
  );

  const errorEl = error === 'SCREEN_PERMISSION_DENIED' ? (
    <div className="descGray" style={{ color: 'var(--danger-text)' }}>
      {t('voice.screenPermissionDenied')}
      {window.cyclone && window.cyclone.openScreenRecordingSettings && (
        <button onClick={() => window.cyclone.openScreenRecordingSettings()} className="ScreenPermissionSettingsBtn secondaryColor">
          {t('voice.openSettings')}
        </button>
      )}
    </div>
  ) : error && <div className="descGray" style={{ color: 'var(--danger-text)' }}>{error}</div>;

  // Local track has no "how loud do I sound to myself" control - setVolume
  // acts on a RemoteAudioTrack, there isn't one for yourself. The transcript
  // bot (bot:stt:) never publishes any track (canPublishSources: [] - see
  // livekitToken.js), unlike the music bot which actually plays audio and
  // needs its volume adjustable - so it's the one bot identity excluded.
  const canAdjustVolume = (p) => !p.isLocal && !p.identity.startsWith('bot:stt:');

  // `extra` sits just before the mic icon - the phone rows put their volume
  // badge there.
  const participantRowContent = (p, occupant, extra) => (
    <>
      <Avatar className="VoiceParticipantAvatar" imageUrl={occupant && occupant.imageUrl} alt="" username={occupant && occupant.username} seed={occupant} />
      {p.speaking && <IoRadioButtonOn style={{ color: 'var(--accent-glow)' }} />}
      <span className="VoiceParticipantName">{p.name || p.identity}</span>
      {p.isLocal && <span className="VoiceParticipantYou">{t('voice.you')}</span>}
      {extra}
      {/* The music bot publishes its stream under
          TrackSource.SOURCE_MICROPHONE (see musicSession.js), so
          isMicrophoneEnabled - and therefore `muted` - is always
          meaningless for it: it's never actually muted/unmuted the
          way a real participant's mic is. */}
      {!p.identity.startsWith('bot:') && (
        <span className={`VoiceParticipantMic ${p.muted ? 'VoiceParticipantMicMuted' : ''}`}>{p.muted ? <IoMicOff /> : <IoMic />}</span>
      )}
    </>
  );

  // LiveKit only knows identity/name; the avatar lives on the room's
  // occupant list from presence:sync, keyed by the same userId the identity
  // carries - same join VideoGridOverlay does for its camera-off fallback.
  const occupantFor = (p) => occupants.find((o) => o.userId === p.identity);

  const deviceSelectsEl = (
    <>
      {devices.length > 1 && (
        <select className="VoiceDeviceSelect" value={selectedDeviceId || ''} onChange={(e) => selectDevice(e.target.value)}>
          {devices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || t('voice.microphone')}</option>)}
        </select>
      )}

      {/* Gated on cameraOn, not just videoDevices.length>1 - device labels
          are blank until camera permission is actually granted, so this
          would otherwise show a dropdown full of blank options before the
          user ever turns their camera on. */}
      {cameraOn && videoDevices.length > 1 && (
        <select className="VoiceDeviceSelect" value={selectedVideoDeviceId || ''} onChange={(e) => selectVideoDevice(e.target.value)}>
          {videoDevices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || t('voice.camera')}</option>)}
        </select>
      )}
    </>
  );

  const musicEl = (
    <div className="MusicNowPlaying">
      {musicState && musicState.status !== 'idle' ? (
        <>
          {musicState.status === 'error' ? (
            <div className="descGray" style={{ color: 'var(--danger-text)' }}>{t('music.error')}</div>
          ) : (
            <div className="MusicNowPlayingRow">
              {musicState.nowPlaying && musicState.nowPlaying.thumbnailUrl ? (
                <img className="MusicNowPlayingThumb" src={musicState.nowPlaying.thumbnailUrl} alt="" />
              ) : (
                <div className="MusicNowPlayingThumb MusicNowPlayingThumbFallback" />
              )}
              <div className="MusicNowPlayingInfo">
                <MarqueeText className="MusicNowPlayingTitle" text={musicState.nowPlaying ? cleanMusicTitle(musicState.nowPlaying.title) : t('music.nowPlaying')} />
                <aside className="descGray MusicNowPlayingStatus">
                  {musicState.status === 'connecting' && <IoSync className="MusicConnectingSpinner" />}
                  {musicState.status === 'connecting' ? t('voice.connecting') : (musicState.status === 'paused' ? t('music.pause') : t('music.nowPlaying'))}
                </aside>
                {musicState.nowPlaying && musicState.nowPlaying.requestedBy && (
                  <aside className="descGray MusicNowPlayingBy">
                    <IoPersonCircle />
                    {t('music.requestedBy')}&nbsp;
                    <b>{musicState.nowPlaying.requestedBy.username}</b>
                  </aside>
                )}
              </div>
            </div>
          )}
          <div className="MusicControls">
            <button
              onClick={() => (musicState.status === 'paused' ? resumeMusic(currentRoomId) : pauseMusic(currentRoomId))}
              className="secondaryColor"
              disabled={musicState.status === 'connecting' || musicState.status === 'error'}
              title={musicState.status === 'paused' ? t('music.resume') : t('music.pause')}
            >
              {musicState.status === 'paused' ? <IoPlay /> : <IoPause />}
            </button>
            <button
              onClick={() => skipMusic(currentRoomId)}
              className="secondaryColor"
              title={t('music.skip')}
            >
              <IoPlaySkipForward />
            </button>
            <button
              onClick={() => setMusicLoop(currentRoomId, !musicState.loop)}
              className={musicState.loop ? 'primaryColor' : 'secondaryColor'}
              title={musicState.loop ? t('music.loopOn') : t('music.loop')}
            >
              <IoRepeat />
            </button>
            <button onClick={() => setShowMusicSearch(true)} className="secondaryColor" title={t('music.searchTitle')}><IoMusicalNotes /></button>
            <button onClick={() => stopMusic(currentRoomId)} className="secondaryColor" title={t('music.stop')}><IoStop /></button>
          </div>
        </>
      ) : (
        <button onClick={() => setShowMusicSearch(true)} className="secondaryColor MusicSearchTrigger" title={t('music.searchTitle')}>
          <IoMusicalNotes /> {t('music.searchTitle')}
        </button>
      )}
    </div>
  );

  const askClearQueue = () => confirm({
    message: t('music.clearQueueConfirm', { count: musicQueue.length }),
    confirmLabel: t('music.clearQueue'),
    onConfirm: () => clearMusicQueue(currentRoomId)
  });

  // In the sheet the queue has the whole music tab to itself - always open,
  // so its header stops being a toggle there.
  const queueOpen = isMobile || showQueue;
  const queueEl = musicQueue.length > 0 && (
    <div className="MusicQueue">
      <div className="MusicQueueHeader">
        <button className="MusicQueueToggle" onClick={() => setShowQueue((v) => !v)} disabled={isMobile}>
          {!isMobile && (showQueue ? <IoChevronUp /> : <IoChevronDown />)}
          {t('music.queueCount', { count: musicQueue.length })}
        </button>
        <button onClick={() => setShowMusicSearch(true)} className="secondaryColor" title={t('music.addToQueue')}><IoAdd /></button>
        <button onClick={() => shuffleMusicQueue(currentRoomId)} className="secondaryColor" title={t('music.shuffleQueue')}><IoShuffle /></button>
        <button onClick={askClearQueue} className="secondaryColor" title={t('music.clearQueue')}><IoTrash /></button>
      </div>

      {queueOpen && (
        <ul className="MusicQueueList">
          {musicQueue.map((entry, i) => (
            <li key={entry.queueId} className="MusicQueueItem">
              <span className="MusicQueueIndex">{i + 1}</span>
              {entry.thumbnailUrl
                ? <img className="MusicQueueThumb" src={entry.thumbnailUrl} alt="" />
                : <div className="MusicQueueThumb MusicNowPlayingThumbFallback" />}
              <div className="MusicQueueInfo">
                <span className="MusicQueueTitle">{cleanMusicTitle(entry.title)}</span>
                {entry.requestedBy && <aside className="descGray">{entry.requestedBy.username}</aside>}
              </div>
              <div className="MusicQueueItemActions">
                <button onClick={() => moveInMusicQueue(currentRoomId, entry.queueId, 'up')} disabled={i === 0} title={t('music.moveUp')}><IoArrowUp /></button>
                <button onClick={() => moveInMusicQueue(currentRoomId, entry.queueId, 'down')} disabled={i === musicQueue.length - 1} title={t('music.moveDown')}><IoArrowDown /></button>
                <button onClick={() => removeFromMusicQueue(currentRoomId, entry.queueId)} title={t('music.removeFromQueue')}><IoClose /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const lyricsEl = (
    <LyricsPanel
      musicState={musicState}
      onExpand={() => setShowLyricsOverlay(true)}
      offsetSec={lyricsSync.offsetSec}
      setOffsetSec={lyricsSync.setOffsetSec}
      alwaysExpanded={isMobile}
    />
  );

  // Visible whenever there's something to read (currently running, or just
  // stopped with a few lines still worth glancing back at) - same "stick
  // around after the fact" shape as .MusicQueue above.
  const transcriptEl = transcriptVisible && (
    <div className="TranscriptPanel">
      <div className="TranscriptPanelHeader">
        <button className="TranscriptPanelToggle" onClick={() => setShowTranscript((v) => !v)} disabled={isMobile}>
          {!isMobile && (showTranscript ? <IoChevronUp /> : <IoChevronDown />)}
          <IoDocumentText />
          {t('transcript.panelTitle')}
        </button>
      </div>

      {transcriptExpanded && (
        <>
          <div className="TranscriptLines" ref={transcriptListRef}>
            {transcriptSegments.length === 0 && transcriptPartialList.length === 0 && (
              <div className="descGray TranscriptEmpty">{t('transcript.empty')}</div>
            )}
            {transcriptSegments.map((seg) => (
              <div key={seg.seq} className="TranscriptLine">
                <b className="TranscriptLineSpeaker">{seg.username}</b>
                <span className="TranscriptLineText">{seg.text}</span>
              </div>
            ))}
            {/* Live, not-yet-finalized speech - softer styling makes it
                read as provisional, distinct from the settled lines
                above it (which won't change again). */}
            {transcriptPartialList.map((p) => (
              <div key={p.userId} className="TranscriptLine TranscriptLinePartial">
                <b className="TranscriptLineSpeaker">{p.username}</b>
                <span className="TranscriptLineText">{p.text}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  // Starting is confirmed (it records everyone in the room, not just the
  // person tapping); stopping never needs to be.
  const toggleTranscript = () => (transcriptActive
    ? stopTranscript(currentRoomId)
    : confirm({ message: t('transcript.startConfirm'), confirmLabel: t('transcript.start'), danger: false, onConfirm: () => startTranscript(currentRoomId) }));

  // ----- Phone: mini bar / side rail + sheet / drawer -----

  const renderMobile = () => {
    const musicPlaying = musicState && musicState.nowPlaying && musicState.status !== 'idle' && musicState.status !== 'error';
    const speakingNames = participants
      .filter((p) => p.speaking && !p.identity.startsWith('bot:'))
      .map((p) => (p.isLocal ? t('voice.you') : (p.name || p.identity)));
    const anySpeaking = participants.some((p) => p.speaking);
    // getDisplayMedia doesn't exist on iOS Safari or Android Chrome - a
    // button that can only ever fail is just noise. Electron (a narrow
    // desktop window) has its own picker, so it keeps the button.
    const canScreenShare = !!(window.cyclone || (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia));
    const tabs = [
      { key: 'people', label: `${t('voiceSheet.tabPeople')} ${participants.length}` },
      { key: 'music', label: t('voiceSheet.tabMusic') },
      { key: 'lyrics', label: t('voiceSheet.tabLyrics') },
      ...(transcriptVisible ? [{ key: 'transcript', label: t('voiceSheet.tabTranscript') }] : [])
    ];

    let subline;
    if (musicPlaying) subline = <><IoMusicalNotes /><span>{cleanMusicTitle(musicState.nowPlaying.title)}</span></>;
    else if (speakingNames.length) subline = <span>{t('voiceSheet.speaking', { names: speakingNames.join(', ') })}</span>;
    else subline = <span>{connected ? `${t('voice.connected')} · ${t('voice.participantCount', { count: participants.length })}` : t('voice.connecting')}</span>;

    return (
      <>
        <div className="VoiceMiniBar glass">
          <button className="VoiceMiniBarMain" onClick={() => setSheetOpen(true)} title={t('voiceSheet.expand')} aria-label={t('voiceSheet.expand')}>
            <span className={`VoicePanelStatusDot VoiceMiniBarDot ${connected ? '' : 'VoicePanelStatusConnecting'} ${anySpeaking ? 'VoiceMiniBarDotSpeaking' : ''}`} />
            <span className="VoiceMiniBarText">
              <b className="VoiceMiniBarTitle">{label}</b>
              <span className="descGray VoiceMiniBarSub">{subline}</span>
            </span>
            <span className="VoiceMiniBarCount"><IoPeople />{participants.length}</span>
            {isMobileLandscape ? <IoChevronBack className="VoiceMiniBarChevron" /> : <IoChevronUp className="VoiceMiniBarChevron" />}
          </button>
          <button onClick={toggleMute} className={`VoiceMiniBarBtn ${muted ? 'cancelColor' : 'primaryColor'}`} title={muted ? t('voice.unmute') : t('voice.mute')}>
            {muted ? <IoMicOff /> : <IoMic />}
          </button>
          <button onClick={leaveRoom} className="VoiceMiniBarBtn cancelColor" title={t('voice.leaveRoom')}><IoExitOutline /></button>
        </div>

        {/* Landscape only (CSS): the drawer leaves the page visible beside
            it, and tapping that page should just close the drawer. */}
        {sheetOpen && <div className="VoiceSheetScrim" onClick={() => setSheetOpen(false)} />}

        {/* Always mounted, hidden with visibility rather than unmounted:
            RoomReactions (in the footer) owns the layer thrown emoji fly
            across, which has to keep working while the sheet is closed. */}
        <div className={`VoiceSheet${sheetOpen ? ' VoiceSheetOpen' : ''}`} aria-hidden={!sheetOpen}>
          <div className="VoiceSheetHeader">
            <button className="VoiceSheetCollapse" onClick={() => setSheetOpen(false)} title={t('voiceSheet.collapse')} aria-label={t('voiceSheet.collapse')}>
              {isMobileLandscape ? <IoChevronForward /> : <IoChevronDown />}
            </button>
            <div className="VoiceSheetTitle">
              <span className="VoicePanelRoomName">{label}</span>
              {statusEl}
            </div>
          </div>

          {recordingBadgeEl}
          {errorEl}

          <div className="VoiceSheetControls">
            <button onClick={toggleMute} className={`VoiceSheetControl ${muted ? 'cancelColor' : 'primaryColor'}`} title={muted ? t('voice.unmute') : t('voice.mute')}>
              {muted ? <IoMicOff /> : <IoMic />}<span>{t('voiceSheet.mic')}</span>
            </button>
            <button onClick={toggleCamera} className={`VoiceSheetControl ${cameraOn ? 'primaryColor' : 'cancelColor'}`} title={cameraOn ? t('voice.cameraOff') : t('voice.cameraOn')}>
              {cameraOn ? <IoVideocam /> : <IoVideocamOff />}<span>{t('voiceSheet.camera')}</span>
            </button>
            {canScreenShare && (
              <button
                onClick={toggleScreenShare}
                disabled={screenSharingActive && !screenShareOn}
                className={`VoiceSheetControl ${screenShareOn ? 'primaryColor' : 'secondaryColor'}`}
                title={screenShareOn ? t('voice.stopScreenShare') : t('voice.startScreenShare')}
              >
                {screenShareOn ? <IoDesktop /> : <IoDesktopOutline />}<span>{t('voiceSheet.screen')}</span>
              </button>
            )}
            {overlayActive && !watchingTogether && (
              <button onClick={() => { setShowVideo((prev) => !prev); setSheetOpen(false); }} className={`VoiceSheetControl ${showVideo ? 'primaryColor' : 'secondaryColor'}`} title={showVideo ? t('voice.hideVideo') : t('voice.showVideo')}>
                <IoGrid /><span>{t('voiceSheet.video')}</span>
              </button>
            )}
            {watchingTogether && watchMinimized && (
              <button onClick={() => { setWatchMinimized(false); setSheetOpen(false); }} className="VoiceSheetControl secondaryColor" title={t('music.showWatchTogether')}>
                <IoTv /><span>{t('voiceSheet.video')}</span>
              </button>
            )}
            <button onClick={toggleTranscript} className={`VoiceSheetControl ${transcriptActive ? 'cancelColor' : 'secondaryColor'}`} title={transcriptActive ? t('transcript.stop') : t('transcript.start')}>
              {transcriptActive ? <IoRecording /> : <IoRecordingOutline />}<span>{t('voiceSheet.transcript')}</span>
            </button>
            <button onClick={leaveRoom} className="VoiceSheetControl cancelColor" title={t('voice.leaveRoom')}>
              <IoExitOutline /><span>{t('voiceSheet.leave')}</span>
            </button>
          </div>

          <div className="VoiceSheetTabs" role="tablist">
            {tabs.map((x) => (
              <button
                key={x.key}
                role="tab"
                aria-selected={tab === x.key}
                className={`VoiceSheetTab${tab === x.key ? ' VoiceSheetTabActive' : ''}`}
                onClick={() => setSheetTab(x.key)}
              >
                {x.label}
              </button>
            ))}
          </div>

          {/* Only rendered while open - nothing in a hidden sheet needs to
              keep measuring marquees or auto-scrolling lyrics. */}
          <div className="VoiceSheetBody">
            {sheetOpen && tab === 'people' && (
              <>
                <div className="VoiceParticipantList">
                  {participants.map((p) => {
                    const adjustable = canAdjustVolume(p);
                    const volumeOpen = adjustable && volumeOpenFor === p.identity;
                    return (
                      <div key={p.identity} className={`VoiceParticipant ${p.speaking ? 'VoiceParticipantSpeaking' : ''}`}>
                        {adjustable ? (
                          <button
                            className="VoiceParticipantRow VoiceParticipantRowButton"
                            onClick={() => setVolumeOpenFor(volumeOpen ? null : p.identity)}
                            aria-expanded={volumeOpen}
                            title={t('voiceSheet.volume')}
                          >
                            {participantRowContent(p, occupantFor(p), (
                              <span className="VoiceParticipantVolumeBadge"><IoVolumeHigh />{Math.round(p.volume * 100)}%</span>
                            ))}
                          </button>
                        ) : (
                          <div className="VoiceParticipantRow">{participantRowContent(p, occupantFor(p))}</div>
                        )}
                        {volumeOpen && (
                          <div className="VoiceParticipantVolume">
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.002"
                              value={volumeToPosition(p.volume)}
                              onChange={(e) => setParticipantVolume(p.identity, positionToVolume(parseFloat(e.target.value)))}
                              aria-label={t('voiceSheet.volume')}
                            />
                            <span className="VoiceParticipantVolumeLabel">{Math.round(p.volume * 100)}%</span>
                            <div className="VoiceVolumePresets">
                              {VOLUME_PRESETS.map((v) => (
                                <button
                                  key={v}
                                  className={Math.abs(p.volume - v) < 0.005 ? 'VoiceVolumePresetActive' : ''}
                                  onClick={() => setParticipantVolume(p.identity, v)}
                                >
                                  {Math.round(v * 100)}%
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {deviceSelectsEl}
              </>
            )}
            {sheetOpen && tab === 'music' && <>{musicEl}{queueEl}</>}
            {sheetOpen && tab === 'lyrics' && (musicState && musicState.lyrics
              ? lyricsEl
              : <div className="descGray VoiceSheetEmpty">{t('voiceSheet.noLyrics')}</div>)}
            {sheetOpen && tab === 'transcript' && transcriptEl}
          </div>

          <div className="VoiceSheetFooter">
            <RoomReactions roomId={currentRoomId} />
          </div>
        </div>
      </>
    );
  };

  // A function rather than inline JSX for the same reason as renderMobile:
  // with both layouts' conditionals inline in one return, the component
  // has so many code paths that eslint's rules-of-hooks path counting
  // overflows and flags every hook above as "called conditionally".
  const renderDesktop = () => (
    <div className="VoicePanel glass">
      <div className="VoicePanelHeader">
        <span className="VoicePanelRoomName">{label}</span>
        {statusEl}
      </div>

      {recordingBadgeEl}

      {errorEl}

      <div className="VoicePanelSectionLabel">{t('voice.inRoom')}</div>
      <div className="VoiceParticipantList">
        {participants.map((p) => (
          <div key={p.identity} className={`VoiceParticipant ${p.speaking ? 'VoiceParticipantSpeaking' : ''}`}>
            <div className="VoiceParticipantRow">
              {participantRowContent(p, occupantFor(p))}
            </div>
            {/* Goes up to 300%: useVoiceConnection connects with
                webAudioMix so this routes through a real Web Audio GainNode
                instead of the native <audio>.volume property, which the
                browser silently clamps to 1 (100%) no matter what's assigned
                to it. The input's own min/max/value are the slider's raw 0-1
                drag position, not the volume itself - positionToVolume/
                volumeToPosition (top of this file) convert between that and
                the real 0-3 value through a knee at 30%, so low percentages
                aren't all crammed into the first couple of drag pixels. */}
            {canAdjustVolume(p) && (
              <div className="VoiceParticipantVolume">
                <IoVolumeHigh />
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.002"
                  value={volumeToPosition(p.volume)}
                  onChange={(e) => setParticipantVolume(p.identity, positionToVolume(parseFloat(e.target.value)))}
                  ref={(el) => { if (el) el.addEventListener('wheel', getVolumeWheelHandler(p.identity), { passive: false }); }}
                />
                <span className="VoiceParticipantVolumeLabel">{Math.round(p.volume * 100)}%</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {deviceSelectsEl}

      <div className="VoicePanelControls">
        <button onClick={toggleMute} className={muted ? 'cancelColor' : 'primaryColor'} title={muted ? t('voice.unmute') : t('voice.mute')}>
          {muted ? <IoMicOff /> : <IoMic />}
        </button>
        <button onClick={toggleCamera} className={cameraOn ? 'primaryColor' : 'cancelColor'} title={cameraOn ? t('voice.cameraOff') : t('voice.cameraOn')}>
          {cameraOn ? <IoVideocam /> : <IoVideocamOff />}
        </button>
        {/* Not hidden by watchingTogether/anyCamera the way the grid toggle
            below is - starting a share is always available, and the overlay
            priority chain (screen-share > watch-together > grid) handles
            whatever else happens to be showing when it starts. Disabled
            (not hidden) while someone else is already sharing - LiveKit
            technically allows more than one concurrent screen-share, but the
            single-spotlight overlay design only ever shows one, so a second
            simultaneous share would just silently have nowhere to go. */}
        <button
          onClick={toggleScreenShare}
          disabled={screenSharingActive && !screenShareOn}
          className={screenShareOn ? 'primaryColor' : 'secondaryColor'}
          title={screenShareOn ? t('voice.stopScreenShare') : t('voice.startScreenShare')}
        >
          {screenShareOn ? <IoDesktop /> : <IoDesktopOutline />}
        </button>
        {/* Hidden while watching together - whatever it would toggle (grid
            or screen-share overlay) is suppressed then too (cameras already
            show in the strip under the video instead), so the button would
            have nothing to act on. Also covers screenSharingActive, not just
            anyCamera - otherwise closing ScreenShareOverlay while nobody's
            camera happened to be on left no way to bring it back short of
            the sharer stopping and restarting. */}
        {overlayActive && !watchingTogether && (
          <button onClick={() => setShowVideo((prev) => !prev)} className={showVideo ? 'primaryColor' : 'secondaryColor'} title={showVideo ? t('voice.hideVideo') : t('voice.showVideo')}>
            <IoGrid />
          </button>
        )}
        {watchingTogether && watchMinimized && (
          <button onClick={() => setWatchMinimized(false)} className="secondaryColor" title={t('music.showWatchTogether')}>
            <IoTv />
          </button>
        )}
        {/* Same trust level as the music bot's controls - anyone in the room
            can start/stop it, not just its owner (see requireInRoom in
            socketServer.js). */}
        <button
          onClick={toggleTranscript}
          className={transcriptActive ? 'cancelColor' : 'secondaryColor'}
          title={transcriptActive ? t('transcript.stop') : t('transcript.start')}
        >
          {transcriptActive ? <IoRecording /> : <IoRecordingOutline />}
        </button>
        <button onClick={leaveRoom} className="cancelColor" title={t('voice.leaveRoom')}><IoExitOutline /></button>
      </div>

      <RoomReactions roomId={currentRoomId} />

      {musicEl}

      {queueEl}

      {lyricsEl}

      {transcriptEl}
    </div>
  );

  return (
    <>
    {isMobile ? renderMobile() : renderDesktop()}
    {/* Rendered as a sibling of .VoicePanel, not nested inside it - .VoicePanel
        is itself a position:fixed 260px box, and Modal's centering
        (position:absolute; top/left:50%) resolves against the nearest
        positioned ancestor. Nested inside .VoicePanel, that ancestor would be
        the 260px box instead of the page, blowing the modal's 10vw padding
        out sideways into a tiny, cut-off, overlapping mess instead of a
        centered dialog. */}
    <MusicSearchModal mState={{ iShow: showMusicSearch, setiShow: setShowMusicSearch }} roomId={currentRoomId} />
    {confirmEl}

    {/* Same sibling-of-.VoicePanel reasoning as MusicSearchModal above.
        Entirely main-process-driven (see ScreenSharePicker.js) - mounted
        unconditionally, stays invisible until electron-starter.js actually
        needs a source picked. */}
    <ScreenSharePicker />

    {/* Exactly one of these three renders at a time - screen-share > watch-
        together > camera grid. An if/else-if/else chain (not three separate
        {cond && <X/>} blocks) is deliberate: it's the only way to GUARANTEE
        the three position:fixed overlays never stack on top of each other,
        rather than relying on each one's own condition happening to exclude
        the others. */}
    {screenSharingActive && showVideo ? (
      <ScreenShareOverlay
        roomLabel={label}
        screenSharer={screenSharer}
        screenTrack={screenShareTracks[screenSharer.identity]}
        screenAudioTrack={screenShareAudioTracks[screenSharer.identity]}
        isLocal={screenSharer.isLocal}
        participants={participants}
        videoTracks={videoTracks}
        occupants={occupants}
        onStop={toggleScreenShare}
        onClose={() => setShowVideo(false)}
      />
    ) : watchingTogether ? (
      <VideoWatchOverlay
        roomLabel={label}
        videoWatchState={videoWatchState}
        participants={participants}
        videoTracks={videoTracks}
        occupants={occupants}
        onPlay={(pos) => playVideo(currentRoomId, pos)}
        onPause={(pos) => pauseVideo(currentRoomId, pos)}
        onSeek={(pos) => seekVideo(currentRoomId, pos)}
        onClose={() => confirm({ message: t('music.stopWatchTogetherConfirm'), confirmLabel: t('music.stopWatchTogether'), onConfirm: () => stopVideo(currentRoomId) })}
        minimized={watchMinimized}
        onMinimize={() => setWatchMinimized(true)}
        onBuffering={() => reportVideoBuffering(currentRoomId)}
        onReady={(pos) => reportVideoReady(currentRoomId, pos)}
      />
    ) : anyCamera && showVideo ? (
      <VideoGridOverlay
        roomLabel={label}
        participants={participants}
        videoTracks={videoTracks}
        occupants={occupants}
        onClose={() => setShowVideo(false)}
      />
    ) : null}

    {showLyricsOverlay && (
      <LyricsOverlay musicState={musicState} onClose={() => setShowLyricsOverlay(false)} offsetSec={lyricsSync.offsetSec} />
    )}
    </>
  );
}
